import { useEffect, useRef, useState, useCallback } from 'react';
import { fetchTimeline, TimelineItem as ApiTimelineItem } from '../api/client';

// ---------------------------------------------------------------------------
// Timeline hook — backed by the unified /api/timeline endpoint.
//
// Before: this hook dual-polled /api/events + /api/activity and merged the
// results client-side into a local TimelineItem shape.
// Now: the hub exposes a single /api/timeline endpoint that already returns
// unified items with a monotonic `sequence`. This hook simply polls that
// endpoint using `after=<latest_seq>` as the cursor.
// ---------------------------------------------------------------------------

// Re-export the API TimelineItem type under the name the rest of the UI uses.
export type TimelineItem = ApiTimelineItem;

// Kept for callers that still reference the old union (now redundant,
// TimelineItem.source plays this role).
export type TimelineKindLegacy = 'event' | 'activity';

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export interface UseEventPollingOptions {
  enabled: boolean;
  intervalMs?: number;
  /** Max items fetched per poll. */
  pageLimit?: number;
}

export interface UseEventPollingResult {
  items: TimelineItem[];
  latestSeq: number;
  isPolling: boolean;
  error: string | null;
  refresh: () => Promise<void>;
}

const DEFAULT_INTERVAL = 4000;
const DEFAULT_PAGE_LIMIT = 100;
const MAX_ITEMS = 300;

/**
 * Polls the unified /api/timeline endpoint and exposes the stream as a
 * sorted list of TimelineItem. The cursor (`latest_seq`) is tracked in a
 * ref so successive polls only request new items.
 */
export function useEventPolling({
  enabled,
  intervalMs = DEFAULT_INTERVAL,
  pageLimit = DEFAULT_PAGE_LIMIT,
}: UseEventPollingOptions): UseEventPollingResult {
  const [items, setItems] = useState<TimelineItem[]>([]);
  const [latestSeq, setLatestSeq] = useState<number>(0);
  const [isPolling, setIsPolling] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // Cursor kept in a ref so the polling callback doesn't need to be
  // re-created whenever latestSeq updates.
  const seqRef = useRef<number>(0);

  const mergeItems = useCallback((incoming: TimelineItem[]) => {
    if (incoming.length === 0) return;
    setItems((prev) => {
      const seen = new Set(prev.map((i) => i.id));
      const fresh = incoming.filter((i) => !seen.has(i.id));
      if (fresh.length === 0) return prev;
      const merged = [...prev, ...fresh].sort((a, b) => a.sequence - b.sequence);
      // keep only the most recent MAX_ITEMS
      return merged.length > MAX_ITEMS ? merged.slice(-MAX_ITEMS) : merged;
    });
  }, []);

  const poll = useCallback(async () => {
    setIsPolling(true);
    try {
      const res = await fetchTimeline(seqRef.current, pageLimit);
      const incoming = res.items ?? [];
      mergeItems(incoming);

      // Advance the cursor. Prefer the server-reported latest_seq but fall
      // back to the max sequence seen in the batch in case the field is
      // missing.
      let nextSeq = seqRef.current;
      if (typeof res.latest_seq === 'number' && res.latest_seq > nextSeq) {
        nextSeq = res.latest_seq;
      }
      for (const it of incoming) {
        if (typeof it.sequence === 'number' && it.sequence > nextSeq) {
          nextSeq = it.sequence;
        }
      }
      if (nextSeq !== seqRef.current) {
        seqRef.current = nextSeq;
        setLatestSeq(nextSeq);
      }
      setError(null);
    } catch (err: any) {
      setError(err?.message ?? String(err));
    } finally {
      setIsPolling(false);
    }
  }, [mergeItems, pageLimit]);

  // Keep a ref to the latest poll to avoid stale-closure issues inside
  // setInterval callbacks.
  const pollRef = useRef(poll);
  useEffect(() => {
    pollRef.current = poll;
  }, [poll]);

  useEffect(() => {
    if (!enabled) return;
    // Kick off an immediate fetch so the panel populates without waiting a
    // full interval.
    pollRef.current();
    const id = setInterval(() => {
      pollRef.current();
    }, intervalMs);
    return () => clearInterval(id);
  }, [enabled, intervalMs]);

  const refresh = useCallback(() => pollRef.current(), []);

  return { items, latestSeq, isPolling, error, refresh };
}
